import type QuillEditor from 'quill';
import type { ReactNode } from 'react';
import { memo, useCallback } from 'react';

type ToolbarProps = {
  editor: QuillEditor;
  className?: string;
  children: ReactNode;
};

function ToolbarImpl({ editor, className, children }: ToolbarProps) {
  const addToolbarToNode = useCallback(
    (node: HTMLDivElement | null) => {
      // React can attach this ref to the same node more than once (StrictMode
      // does on every mount), and each toolbar module Quill creates binds its
      // own click handlers, so a second one would apply every format twice.
      if (!node || editor.modules.toolbar?.container === node) return;
      editor.addModule('toolbar', { container: node });
    },
    [editor]
  );

  return (
    <div ref={addToolbarToNode} className={className}>
      {children}
    </div>
  );
}

// The toolbar's state is kept in Quill, which directly mutates DOM to apply
// new states, so a re-render on prop changes would do nothing useful — the
// memo comparator always reports "no change" to skip it, same as the class
// original's `shouldComponentUpdate() { return false; }`.
export const Toolbar = memo(ToolbarImpl, () => true);
Toolbar.displayName = 'RichTextEditor_Toolbar';
